// HubSpot serverless-function client.
//
// All calls are ROOT-RELATIVE (`/_hcms/api/<Fn>`) so the one compiled bundle
// works in both the production and sandbox portals — the path resolves against
// whichever portal serves the page. Never hardcode an absolute host here (see
// AGENTS.md → Environment configuration).

/** Build the root-relative path for a serverless function. */
export function apiUrl(fn: string): string {
  return `/_hcms/api/${fn}`
}

export interface ApiResult<T = unknown> {
  status: number
  ok: boolean
  data: T
}

/**
 * POST a JSON body to a serverless function and parse the JSON response.
 * Returns status/ok/data without throwing on non-2xx (mirrors the manual
 * `fetch` + `response.status` checks scattered through the legacy modules).
 */
export async function apiPost<T = unknown>(fn: string, body: unknown): Promise<ApiResult<T>> {
  const response = await fetch(apiUrl(fn), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })

  let data: T
  try {
    data = (await response.json()) as T
  } catch {
    data = undefined as unknown as T
  }

  return { status: response.status, ok: response.ok, data }
}

// ─── Named helpers ────────────────────────────────────────────────────────────
// Thin wrappers over apiPost for the functions duplicated most across modules.
// Prefer these over bare apiPost so the function name is a compile-time symbol.

export const getContactRecordBasedOnEmail = <T = unknown>(body: unknown) =>
  apiPost<T>("GetContactRecordBasedOnEmail", body)

export const addContactRecordInHubspot = <T = unknown>(body: unknown) => apiPost<T>("AddContactRecordInHubspot", body)

export const updateContactRecord = <T = unknown>(body: unknown) => apiPost<T>("UpdateContactRecord", body)

export const getDealRecordBasedOnEmail = <T = unknown>(body: unknown) => apiPost<T>("GetDealRecordBasedOnEmail", body)

export const addDealRecord = <T = unknown>(body: unknown) => apiPost<T>("AddDealRecord", body)

export const updateDealRecord = <T = unknown>(body: unknown) => apiPost<T>("UpdateDealRecord", body)

export const searchUANValuesInDealRecord = <T = unknown>(body: unknown) =>
  apiPost<T>("SearchUANValuesInDealRecord", body)

export const fetchAssociatedDealsBasedOnContactId = <T = unknown>(body: unknown) =>
  apiPost<T>("FetchAssociatedDealsBasedOnContactId", body)

export const dealAssociateWithContact = <T = unknown>(body: unknown) => apiPost<T>("dealAssociateWithContact", body)

export const logToNautilus = <T = unknown>(body: unknown) => apiPost<T>("LogToNautilus", body)
